import * as React from 'react';

import * as Sentry from '@sentry/react';

import { setLocation } from 'js/lib/coursera.redirect';
import { useLocation } from 'js/lib/useRouter';
import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import { useNaptime } from 'bundles/naptimejs';
import ProgramMembershipsV2 from 'bundles/naptimejs/resources/programMemberships.v2';
import ProgramSwitcherSelectionsV1 from 'bundles/naptimejs/resources/programSwitcherSelections.v1';
import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useEnterpriseAutoEnrolledCourses } from 'bundles/unified-common/contexts/EnterpriseAutoEnrolledCoursesContext';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseSpecializationPageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffSpecializationContext';
import EnterpriseLearnerFlowOptimizationContext from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseLearnerFlowOptimizationContext';
import useEnterpriseCourseEnrollment from 'bundles/unified-description-page-common/hooks/useEnterpriseCourseEnrollment';
import useGuidedCourseNextStepsLazy from 'bundles/unified-description-page-common/hooks/useGuidedCourseNextStepsLazy';
import { getCourseProductUrl } from 'bundles/unified-description-page-common/utils';

export const EnterpriseLearnerFlowOptimizationDataProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const programAutoEnrollmentData = useEnterpriseAutoEnrolledCourses();
  const coursePageData = useEnterpriseCoursePageData();
  const specializationPageData = useEnterpriseSpecializationPageData();
  const program = useProgram();
  const { membership } = useProgramMembership();
  const modalManager = useEnterpriseModalManager();
  const { naptime } = useNaptime(() => ({}));
  const { isExternal } = location.query;
  const { fetchNextSteps } = useGuidedCourseNextStepsLazy();
  const userId = getUser().id;

  const isComingFromExternalPlatforms = isExternal === 'true'; // eg. LMS/LXPs
  const programMembershipV2Instance = new ProgramMembershipsV2(membership || {});
  const isEligibleToJoinProgram = programMembershipV2Instance.isEligibleToJoinProgram;
  const productId = coursePageData?.id ? coursePageData.id : (specializationPageData?.id as string);
  const showOptimizedLearnerFlow =
    !!features?.enableOptimizedLearnerFlow && isComingFromExternalPlatforms && isAuthenticatedUser();
  const isCourseIncludedInAutoEnrollTrigger = programAutoEnrollmentData?.map((_) => _.id)?.includes(productId);
  const showOptimisedAutoEnrollmentFlow = showOptimizedLearnerFlow && isCourseIncludedInAutoEnrollTrigger;
  const firstCourseSlug = coursePageData?.slug || specializationPageData?.courses?.[0]?.slug;

  // In optimised autoenrollment, if the current XDP course is set be auto-enrolled with the current program (from slug),
  // We directly enroll users into the program and as a result, user gets enrolled in the course
  // Post this, we navigate the user to the next step in the course
  const isOptimisedAutoEnrollmentPossible =
    isEligibleToJoinProgram &&
    showOptimisedAutoEnrollmentFlow &&
    // This handles the case where we wait for user to accept TnC before starting to autoEnroll in the course.
    !modalManager?.isLoading &&
    modalManager?.modalToShow !== ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE;

  const onActionSuccess = React.useCallback<VoidFunction>(async () => {
    const baseHomeUrl = getCourseProductUrl(firstCourseSlug || '', false);
    const courseHome = `${baseHomeUrl}/home`;
    let resourcePath;
    try {
      resourcePath = await fetchNextSteps(firstCourseSlug);
      await naptime.executeMutation(
        ProgramSwitcherSelectionsV1.update(userId, {
          selectionType: 'PROGRAM',
          programId: program?.id,
        })
      );
    } catch (err) {
      Sentry?.captureException(err, {
        extra: { message: '[Learner flow optimization] Error navigating to next item after auto enrollment' },
      });
    } finally {
      setLocation(resourcePath || courseHome);
    }

    // naptime reference changes multiple times during re-renders, which results into this useCallback re-executing again.
    // Which results into cascade effect of useCallbacks re-executing down the line.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNextSteps, firstCourseSlug, program?.id]);

  const [enrollIntoCourse, { error, programJoinErrorCode, isEnrolling }] =
    useEnterpriseCourseEnrollment(onActionSuccess);

  // Every time there is dependency update in theReact.useLayoutEffect, it runs before the browser paints the screen.
  // This helps us set the isLoading faster than useEffect - which runs after render might result into flickering loading screen.
  React.useLayoutEffect(() => {
    if (isOptimisedAutoEnrollmentPossible) {
      enrollIntoCourse({ isAutoEnrollment: true });
    }
  }, [isOptimisedAutoEnrollmentPossible, enrollIntoCourse]);

  return (
    <EnterpriseLearnerFlowOptimizationContext.Provider
      value={{
        error, // contains any other errors that are not covered by programJoinErrorCode
        isEnrolling,
        programJoinErrorCode,
        showOptimizedLearnerFlow,
        enrollIntoCourse,
        onJoiningSuccess: onActionSuccess,
      }}
    >
      {children}
    </EnterpriseLearnerFlowOptimizationContext.Provider>
  );
};
